import React, { useState, useEffect, useCallback } from 'react';
import { gadmService, type GADMZone } from '../services/gadmService';
import { useDebounce } from '../hooks/useDebounce';

interface ZoneSelectorProps {
  onZoneSelected: (zone: GADMZone | null) => void;
  initialZone?: GADMZone | null;
}

const ZoneSelector: React.FC<ZoneSelectorProps> = ({ onZoneSelected, initialZone }) => {
  const [searchTerm, setSearchTerm] = useState(initialZone?.name || '');
  const [searchResults, setSearchResults] = useState<GADMZone[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedZone, setSelectedZone] = useState<GADMZone | null>(initialZone || null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    try {
      const results = await gadmService.searchZones(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Failed to fetch GADM zones:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      handleSearch(debouncedSearchTerm);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm, handleSearch]);

  const handleSelectZone = (zone: GADMZone) => {
    setSelectedZone(zone);
    setSearchTerm(zone.name);
    setSearchResults([]); // Clear search results after selection
    onZoneSelected(zone);
  };

  const handleClearSelection = () => {
    setSelectedZone(null);
    setSearchTerm('');
    onZoneSelected(null);
  };

  return (
    <div className="relative">
      <label htmlFor="zone-search" className="block text-sm font-medium text-white/70">Search GADM Zone</label>
      <input
        id="zone-search"
        type="text"
        className="mt-1 block w-full bg-black text-white placeholder-white/50 px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        placeholder="e.g., Madrid, Barcelona"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {loading && <p className="text-sm text-white/50">Loading...</p>}
      {searchResults.length > 0 && (
        <ul className="absolute z-10 w-full bg-black border border-white/20 rounded-md shadow-lg max-h-60 overflow-auto mt-1">
          {searchResults.map((zone) => (
            <li
              key={zone.id}
              className="px-3 py-2 cursor-pointer hover:bg-white/10 text-white"
              onClick={() => handleSelectZone(zone)}
            >
              {zone.name}
            </li>
          ))}
        </ul>
      )}
      {selectedZone && (
        <div className="mt-2 p-2 border border-green-500/50 bg-green-900/30 rounded-md flex justify-between items-center">
          <p className="text-sm text-green-300">Selected Zone: <strong>{selectedZone.name}</strong> ({selectedZone.id})</p>
          <button
            onClick={handleClearSelection}
            className="ml-4 px-2 py-1 border border-transparent text-sm font-medium rounded-md text-red-300 bg-red-900/40 hover:bg-red-800/60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Remove Selected Zone
          </button>
        </div>
      )}
    </div>
  );
};

export default ZoneSelector;
